/**
 * Iter 68 — Admin Vendors filter panel.
 *
 * Renders an expandable "Advanced filters" toggle button that, when open,
 * exposes 8 filter facets driven by the enriched /api/admin/vendors payload:
 *   1. Approval status   — pending / approved / rejected
 *   2. Listing status    — active / suspended / paused
 *   3. Subscription      — active / past_due / cancelled / trialing / none
 *   4. City              — populated from the data (top 12 distinct)
 *   5. Fleet size        — 0 / 1-5 / 6-20 / 20+
 *   6. 2FA               — enabled / disabled
 *   7. No-show severity  — 0 / 1-2 / ≥3 in 12mo
 *   8. Created           — last 7d / 30d / 90d / all
 *
 * State is fully controlled (parent owns the `filters` object) so the
 * vendor list filtering logic stays in one place (AdminDashboard.jsx
 * `filteredVendors` memo).
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Filter, X, ChevronDown, ChevronUp } from "lucide-react";

export const EMPTY_VENDOR_FILTERS = Object.freeze({
  approval: "all",
  listing: "all",
  subscription: "all",
  city: "all",
  fleet: "all",
  twofa: "all",
  noShow: "all",
  created: "all",
});

/**
 * Pure helper — takes the vendor array + a `filters` object and returns
 * the subset that matches. Exported so the parent dashboard's `useMemo`
 * can use it directly.
 */
export function applyVendorFilters(vendors, filters) {
  return vendors.filter((v) => {
    if (filters.approval !== "all" && (v.vendor_status || "pending") !== filters.approval) return false;
    if (filters.listing !== "all" && (v.listing_status || "active") !== filters.listing) return false;
    if (filters.subscription !== "all") {
      const s = v.subscription_status || "none";
      if (s !== filters.subscription) return false;
    }
    if (filters.city !== "all" && (v.business_city || "—") !== filters.city) return false;
    if (filters.fleet !== "all") {
      const n = Number(v.fleet_size || 0);
      if (filters.fleet === "0" && n !== 0) return false;
      if (filters.fleet === "1-5" && (n < 1 || n > 5)) return false;
      if (filters.fleet === "6-20" && (n < 6 || n > 20)) return false;
      if (filters.fleet === "20+" && n <= 20) return false;
    }
    if (filters.twofa !== "all") {
      const on = !!v.totp_2fa_enabled;
      if (filters.twofa === "enabled" && !on) return false;
      if (filters.twofa === "disabled" && on) return false;
    }
    if (filters.noShow !== "all") {
      const n = Number(v.no_show_count_12m || 0);
      if (filters.noShow === "0" && n !== 0) return false;
      if (filters.noShow === "1-2" && (n < 1 || n > 2)) return false;
      if (filters.noShow === "3+" && n < 3) return false;
    }
    if (filters.created !== "all") {
      const created = v.created_at ? Date.parse(v.created_at) : 0;
      const days = filters.created === "7d" ? 7 : filters.created === "30d" ? 30 : filters.created === "90d" ? 90 : 0;
      if (days > 0 && (Date.now() - created) > days * 86400000) return false;
    }
    return true;
  });
}

export default function AdminVendorFilters({ vendors, filters, onChange }) {
  const [open, setOpen] = useState(false);

  // Surface the top-12 distinct business_city values from the data so the
  // dropdown reflects what's actually in the DB rather than a hardcoded list.
  const cities = useMemo(() => {
    const counts = new Map();
    for (const v of vendors) {
      const c = (v.business_city || "").trim();
      if (!c) continue;
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([c]) => c);
  }, [vendors]);

  // Build a "active facet count" so the user sees at a glance how many
  // filters are in play. Useful when the panel is collapsed.
  const activeCount = useMemo(
    () => Object.values(filters).filter((v) => v !== "all").length,
    [filters],
  );

  const setOne = (key, value) => onChange({ ...filters, [key]: value });
  const reset = () => onChange({ ...EMPTY_VENDOR_FILTERS });

  return (
    <div className="space-y-3" data-testid="admin-vendor-filters">
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen((o) => !o)}
          data-testid="admin-vendor-filters-toggle"
          className="rounded-full"
        >
          <Filter className="h-3.5 w-3.5 mr-1.5" />
          Advanced filters
          {activeCount > 0 && (
            <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-800 border-blue-200">
              {activeCount}
            </Badge>
          )}
          {open ? <ChevronUp className="h-3.5 w-3.5 ml-1" /> : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
        </Button>
        {activeCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={reset}
            className="rounded-full text-neutral-600 hover:text-neutral-900"
            data-testid="admin-vendor-filters-reset"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 rounded-2xl border border-border bg-neutral-50/60" data-testid="admin-vendor-filters-panel">
          <FacetSelect
            label="Approval"
            testid="filter-approval"
            value={filters.approval}
            onChange={(v) => setOne("approval", v)}
            options={[
              ["all", "All"],
              ["pending", "Pending"],
              ["approved", "Approved"],
              ["rejected", "Rejected"],
            ]}
          />
          <FacetSelect
            label="Listing status"
            testid="filter-listing"
            value={filters.listing}
            onChange={(v) => setOne("listing", v)}
            options={[
              ["all", "All"],
              ["active", "Active"],
              ["suspended", "Suspended"],
              ["paused", "Paused"],
            ]}
          />
          <FacetSelect
            label="Subscription"
            testid="filter-subscription"
            value={filters.subscription}
            onChange={(v) => setOne("subscription", v)}
            options={[
              ["all", "All"],
              ["active", "Active"],
              ["trialing", "Trialing"],
              ["past_due", "Past due"],
              ["cancelled", "Cancelled"],
              ["manual", "Manual"],
              ["none", "None"],
            ]}
          />
          <FacetSelect
            label="City"
            testid="filter-city"
            value={filters.city}
            onChange={(v) => setOne("city", v)}
            options={[["all", "All cities"], ...cities.map((c) => [c, c])]}
          />
          <FacetSelect
            label="Fleet size"
            testid="filter-fleet"
            value={filters.fleet}
            onChange={(v) => setOne("fleet", v)}
            options={[
              ["all", "All"],
              ["0", "0 cars"],
              ["1-5", "1-5"],
              ["6-20", "6-20"],
              ["20+", "20+"],
            ]}
          />
          <FacetSelect
            label="2FA"
            testid="filter-twofa"
            value={filters.twofa}
            onChange={(v) => setOne("twofa", v)}
            options={[
              ["all", "All"],
              ["enabled", "Enabled"],
              ["disabled", "Disabled"],
            ]}
          />
          <FacetSelect
            label="No-shows (12mo)"
            testid="filter-no-show"
            value={filters.noShow}
            onChange={(v) => setOne("noShow", v)}
            options={[
              ["all", "All"],
              ["0", "Clean (0)"],
              ["1-2", "1-2"],
              ["3+", "Repeat (3+)"],
            ]}
          />
          <FacetSelect
            label="Created"
            testid="filter-created"
            value={filters.created}
            onChange={(v) => setOne("created", v)}
            options={[
              ["all", "Any time"],
              ["7d", "Last 7 days"],
              ["30d", "Last 30 days"],
              ["90d", "Last 90 days"],
            ]}
          />
        </div>
      )}
    </div>
  );
}

function FacetSelect({ label, testid, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-neutral-600">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(([val, label]) => (
            <SelectItem key={val} value={val}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
